import appManager from '@app/appManager';
import { random, clamp } from 'lodash';

type GraphQlErrorLike = {
  extensions?: {
    problemDetails?: {
      status?: number;
    };
  };
};

function getStatusCodeFromGraphQlError(
  errorOrResult: unknown,
): number | undefined {
  const graphQlErrors: GraphQlErrorLike[] | undefined =
    (errorOrResult as any)?.graphQLErrors ?? (errorOrResult as any)?.errors;

  const first = Array.isArray(graphQlErrors) ? graphQlErrors[0] : undefined;
  return first?.extensions?.problemDetails?.status;
}

function shouldRetryForStatus(statusCode: number | undefined): boolean {
  if (typeof statusCode !== 'number') return false;
  return statusCode === 408 || (statusCode >= 500 && statusCode <= 599);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export interface RetryOptions {
  maxAttempts?: number;
  // Function that calculates the delay in milliseconds before the next retry attempt.
  // Receives the attempt number (1-indexed) and should return the delay duration.
  getBackoffDelay?: (attempt: number) => number; // attempt is 1-indexed
  operationName?: string;
  // Function that determines whether to retry based on the error or result.
  // Receives the error object or GraphQL result and should return true to retry, false to stop.
  shouldRetryForGraphQLErrors?: (errorOrResult: unknown) => boolean;
}

export const retryDefaultDelay = (attempt: number): number => {
  const base = 300 * (1 << (attempt - 1)); // 300 * 2^(attempt-1)
  const jittered = random(0, 2 * base, true); // float in [0, 2*base]
  return clamp(jittered, 0, 30000); // cap at 30s
};

/**
 * Retries a mutation or request function when a GraphQL error
 * occurs. Determines whether to retry or not using the shouldRetryForGraphQlErrors function.
 */
export async function retryMutation<T>(
  mutateFn: () => Promise<T>,
  options: RetryOptions = {},
): Promise<T> {
  const DEFAULT_MAX_RETRY_ATTEMPTS = 5;
  const maxAttempts = options.maxAttempts ?? DEFAULT_MAX_RETRY_ATTEMPTS;
  const backoff = options.getBackoffDelay ?? retryDefaultDelay;
  const opName = options.operationName ?? 'unknown';
  const shouldRetryFn =
    options.shouldRetryForGraphQLErrors ??
    ((errorOrResult: unknown) =>
      shouldRetryForStatus(getStatusCodeFromGraphQlError(errorOrResult)));

  let lastResult: any;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      lastResult = await mutateFn();

      const statusFromErrors = getStatusCodeFromGraphQlError(lastResult);
      if (!shouldRetryFn(lastResult)) {
        return lastResult as T;
      }

      if (attempt < maxAttempts) {
        appManager
          .logger('api')
          .warn(
            statusFromErrors
              ? `retrying mutation request error for '${opName}' (HTTP ${statusFromErrors})`
              : `retrying mutation request error for '${opName}'`,
            lastResult,
          );
        await sleep(backoff(attempt));
        continue;
      }

      return lastResult as T;
    } catch (error) {
      const statusFromError = getStatusCodeFromGraphQlError(error);
      if (shouldRetryFn(error) && attempt < maxAttempts) {
        appManager
          .logger('api')
          .warn(
            statusFromError
              ? `retrying mutation request error for '${opName}' (HTTP ${statusFromError})`
              : `retrying mutation request error for '${opName}'`,
            error,
          );
        await sleep(backoff(attempt));
        continue;
      }
      throw error;
    }
  }

  return lastResult as T;
}
